import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import nurseryService from '../../services/nursery';
import purchaseOrderService from '../../services/purchaseOrders';
import { errorMessage } from '../../utils/apiResponse';
import type { StockItemBarcode } from '../../types/nursery';
import type { Supplier } from '../../types/purchaseOrders';

/**
 * Suppliers' tag barcodes for one stock item. A bought-in plant arrives with
 * its grower's printed tag, and scanning that tag has to find the item, so
 * each supplier's code is kept beside the item's own. One code names one
 * item in the organisation; the server refuses a clash and names the code.
 *
 * The supplier list needs purchase-order or accounts access. Without it the
 * codes are still kept, just without saying whose tag they are.
 */
export function StockItemBarcodes({ stockItemId }: { stockItemId: number }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'StockItemBarcodes' });
  const [rows, setRows] = useState<StockItemBarcode[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    nurseryService.getStockItemBarcodes(stockItemId)
      .then(res => setRows(res.success ? res.data || [] : []))
      .catch(() => setRows([]));
    purchaseOrderService.listSuppliers()
      .then(res => { if (res.success) setSuppliers(res.data || []); })
      .catch(() => { /* no access: codes without a supplier */ });
  }, [stockItemId]);

  const update = (i: number, patch: Partial<StockItemBarcode>) => {
    setRows(prev => (prev || []).map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setMessage(null);
  };

  const save = async () => {
    if (!rows) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await nurseryService.setStockItemBarcodes(
        stockItemId,
        rows.filter(r => r.barcode.trim()),
      );
      if (res.success) {
        setRows(res.data || []);
        setMessage({ ok: true, text: t('saved') });
      } else {
        setMessage({ ok: false, text: errorMessage(res, t('saveFailed')) });
      }
    } catch (e) {
      setMessage({ ok: false, text: errorMessage(e, t('saveFailed')) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-6 space-y-3" aria-labelledby="supplier-barcodes">
      <div>
        <h2 id="supplier-barcodes" className="text-sm font-semibold text-neutral-800">{t('title')}</h2>
        <p className="text-xs text-neutral-500">
          {t('intro')}
        </p>
      </div>

      {rows == null ? (
        <p className="text-sm text-neutral-400">{t('loading')}</p>
      ) : (
        <>
          {rows.length === 0 && <p className="text-sm text-neutral-500">{t('none')}</p>}
          <ul className="space-y-2">
            {rows.map((r, i) => (
              <li key={i} className="flex flex-col sm:flex-row gap-2">
                <label className="sr-only" htmlFor={`supplier-barcode-${i}`}>{t('barcode')}</label>
                <input
                  id={`supplier-barcode-${i}`}
                  value={r.barcode}
                  onChange={e => update(i, { barcode: e.target.value })}
                  inputMode="numeric"
                  maxLength={30}
                  placeholder={t('barcode')}
                  className="flex-1 min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                />
                <div className="flex gap-2 min-w-0">
                  <label className="sr-only" htmlFor={`supplier-barcode-supplier-${i}`}>{t('supplier')}</label>
                  <select
                    id={`supplier-barcode-supplier-${i}`}
                    value={r.supplier_id ?? ''}
                    onChange={e => update(i, {
                      supplier_id: e.target.value ? Number(e.target.value) : null,
                      supplier_name: null,
                    })}
                    className="flex-1 min-w-0 sm:w-56 border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">{r.supplier_id && r.supplier_name ? r.supplier_name : t('noSupplier')}</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <button
                    type="button"
                    aria-label={t('remove')}
                    onClick={() => { setRows(prev => (prev || []).filter((_, j) => j !== i)); setMessage(null); }}
                    className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setRows(prev => [...(prev || []), { barcode: '', supplier_id: null }])}
            >
              <Plus className="h-4 w-4" /> {t('add')}
            </Button>
            <Button type="button" size="sm" variant="primary" disabled={busy} onClick={() => void save()}>
              {busy ? t('saving') : t('save')}
            </Button>
          </div>
          {message && (
            <p className={message.ok ? 'text-sm text-green-700' : 'text-sm text-red-600'}>{message.text}</p>
          )}
        </>
      )}
    </section>
  );
}
